import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
import React, { memo, useEffect, useRef, useState } from "react";
import { useIsomorphicLayoutEffect } from "@swiggy-private/core-ui";
import { QUICK_CHECKOUT_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { CTA_HIDE_MS } from "./animationTiming";
import {
    Container,
    Content,
    SavingsPill,
    PillSolidSurface,
    PillGradientSurface,
    PillContent,
    HeaderCta,
    HiddenMeasure,
    Divider,
    IconWrapper,
    DeliveryCountTextWrapper,
} from "./styles";

import { GenericIcon } from "@swiggy-private/dls-web-icons/dist/GenericIconWeb";
import { default as chevronRightIcon } from "@swiggy-private/dls-web-icons/dist/web/generic/chevronRight/12";

const MEASURE_BUFFER_PX = 4;

interface QuickCheckoutHeaderProps {
    savingsText?: string;
    /** Collapsed copy used only for width measurement (not the animated visible text). */
    collapsedSavingsText?: string;
    /** Multi-delivery copy, e.g. "2 Deliveries". Omitted when count ≤ 1. */
    deliveryCountText?: string;
    headerCtaText?: string;
    onHeaderCtaClick?: () => void;
    isSavingsStripExpanded?: boolean;
    isViewCartHidden?: boolean;
    isLoading?: boolean;
    hasSavings?: boolean;
}

const readContentWidthPx = (element: HTMLElement | null): number => {
    if (!element) {
        return 0;
    }
    return Math.ceil(element.scrollWidth) + MEASURE_BUFFER_PX;
};

const QuickCheckoutHeader = (props: QuickCheckoutHeaderProps) => {
    const {
        savingsText,
        collapsedSavingsText,
        deliveryCountText,
        headerCtaText,
        onHeaderCtaClick,
        isSavingsStripExpanded = false,
        isViewCartHidden = false,
        isLoading = false,
        hasSavings = false,
    } = props;
    const [collapsedMaxWidthPx, setCollapsedMaxWidthPx] = useState(0);
    const [expandedMaxWidthPx, setExpandedMaxWidthPx] = useState(0);
    const [ctaMaxWidthPx, setCtaMaxWidthPx] = useState(0);
    // Off on first mount so View Cart does not wipe in; on only for expand-sequence hide/show.
    const [animateCtaMaxWidth, setAnimateCtaMaxWidth] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const wasViewCartHiddenRef = useRef(isViewCartHidden);

    const isLayoutAnimating = isSavingsStripExpanded || isViewCartHidden;

    useIsomorphicLayoutEffect(() => {
        // Skip while widths are locked for the in-flight animation.
        if (isLayoutAnimating) {
            return;
        }

        let cancelled = false;

        const measure = () => {
            if (cancelled) {
                return;
            }

            if (contentRef.current) {
                const style = window.getComputedStyle(contentRef.current);
                const horizontalPadding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight);
                setExpandedMaxWidthPx(contentRef.current.clientWidth - horizontalPadding);
            }

            setCollapsedMaxWidthPx(readContentWidthPx(measureRef.current));
            // Measure the real CTA (text + chevron) so wipe-in max-width matches settled size.
            setCtaMaxWidthPx(readContentWidthPx(ctaRef.current));
        };

        measure();
        document.fonts?.ready.then(measure).catch(() => undefined);

        return () => {
            cancelled = true;
        };
    }, [collapsedSavingsText, headerCtaText, isLayoutAnimating]);

    useEffect(() => {
        if (isViewCartHidden) {
            setAnimateCtaMaxWidth(true);
            wasViewCartHiddenRef.current = true;
            return;
        }

        if (!wasViewCartHiddenRef.current) {
            return;
        }

        // Keep max-width transition on through the reveal, then disable for later mounts/updates.
        wasViewCartHiddenRef.current = false;
        const disableTransitionTimeoutId = setTimeout(() => {
            setAnimateCtaMaxWidth(false);
        }, CTA_HIDE_MS);

        return () => clearTimeout(disableTransitionTimeoutId);
    }, [isViewCartHidden]);

    return (
        <Container>
            <Content ref={contentRef}>
                {hasSavings && (
                    <SavingsPill
                        isVisibility={!!savingsText}
                        isExpanded={isSavingsStripExpanded}
                        isLayoutAnimating={isLayoutAnimating}
                        collapsedMaxWidthPx={collapsedMaxWidthPx}
                        expandedMaxWidthPx={expandedMaxWidthPx}
                    >
                        <PillSolidSurface isExpanded={isSavingsStripExpanded} aria-hidden />
                        <PillGradientSurface isExpanded={isSavingsStripExpanded} aria-hidden />
                        <PillContent>
                            <Text maxLines={1} color="positive" variant="V2.body2.neutral.bold">
                                {savingsText}
                            </Text>
                        </PillContent>
                    </SavingsPill>
                )}
                {deliveryCountText && (
                    <DeliveryCountTextWrapper isHidden={isViewCartHidden}>
                        <Text maxLines={1} color="text_Med_Emphasis" variant="V2.body2.neutral.medium">
                            {deliveryCountText}
                        </Text>
                    </DeliveryCountTextWrapper>
                )}

                <HeaderCta
                    ref={ctaRef}
                    data-testid={QUICK_CHECKOUT_TEST_IDS.HEADER_CTA}
                    isLoading={isLoading}
                    isHidden={isViewCartHidden}
                    contentMaxWidthPx={ctaMaxWidthPx}
                    animateMaxWidth={animateCtaMaxWidth}
                    onClick={onHeaderCtaClick}
                >
                    <Text maxLines={1} color="text_Low_Emphasis" variant="V2.body2.neutral.regular">
                        {headerCtaText}
                    </Text>
                    <IconWrapper>
                        <GenericIcon Icon={chevronRightIcon} fillColor="text_Low_Emphasis" />
                    </IconWrapper>
                </HeaderCta>
            </Content>
            <HiddenMeasure ref={measureRef} aria-hidden>
                <Text maxLines={1} color="positive" variant="V2.body1.neutral.bold">
                    {collapsedSavingsText}
                </Text>
            </HiddenMeasure>
            <Divider />
        </Container>
    );
};

export default memo(QuickCheckoutHeader);
